import {
  createTheme,
  PaletteColorOptions,
  Palette,
  PaletteColor,
} from "@mui/material/styles";
import "./chipOverrides";

declare module "@mui/material/styles" {
  interface Palette {
    void: PaletteColor;
    holding: PaletteColor;
    primaryOrderStatus: {
      draft: string;
      rejected: string;
      processing: string;
      complete: string;
      void: string;
    };
    approvalStatus: {
      approved: string;
      pending: string;
      rejected: string;
      void: string;
    };
    soFulfillmentStatus: {
      approval: string;
      rejected: string;
      fulfill: string;
      partial: string;
      full: string;
      complete: string;
      void: string;
    };
    poReceiptStatus: {
      approval: string;
      rejected: string;
      receipt: string;
      partial: string;
      full: string;
      complete: string;
      void: string;
    };
    woBuildStatus: {
      rejected: string;
      complete: string;
      void: string;
      approval: string;
      ready: string;
      process: string;
    };
    woPartStatus: {
      pending: string;
      partial: string;
      complete: string;
      void: string;
    };
    buttonColors: {
      good: string;
      bad: string;
    };
  }

  interface PaletteOptions {
    void: PaletteColorOptions;
    holding: PaletteColorOptions;
    primaryOrderStatus: {
      draft: string;
      rejected: string;
      processing: string;
      complete: string;
      void: string;
    };
    approvalStatus: {
      approved: string;
      pending: string;
      rejected: string;
      void: string;
    };
    soFulfillmentStatus: {
      approval: string;
      rejected: string;
      fulfill: string;
      partial: string;
      full: string;
      complete: string;
      void: string;
    };
    poReceiptStatus: {
      approval: string;
      rejected: string;
      receipt: string;
      partial: string;
      full: string;
      complete: string;
      void: string;
    };
    woBuildStatus: {
      rejected: string;
      complete: string;
      void: string;
      approval: string;
      ready: string;
      process: string;
    };
    woPartStatus: {
      pending: string;
      partial: string;
      complete: string;
      void: string;
    };
    buttonColors: {
      good: string;
      bad: string;
    };
  }
}

// Centese Brand Colors
const CENTESE_PRIMARY_GREEN = "#008675"; // Pantone 327C
const CENTESE_ACCENT_GRAY = "#75787B"; // Cool Gray 9C

export const theme = createTheme({
  palette: {
    primary: {
      main: CENTESE_PRIMARY_GREEN,
      light: "#4db6a8", // Lighter shade of primary green
      dark: "#005a4f", // Darker shade of primary green
      contrastText: "#ffffff",
    },
    secondary: {
      main: CENTESE_ACCENT_GRAY,
      light: "#a1a4a7", // Lighter shade of accent gray
      dark: "#4a4d50", // Darker shade of accent gray
      contrastText: "#ffffff",
    },
    background: {
      default: "#f8f9fa",
      paper: "#ffffff",
    },
    text: {
      primary: "#2c2c2c",
      secondary: CENTESE_ACCENT_GRAY,
    },
    grey: {
      50: "#fafafa",
      100: "#f5f5f5",
      200: "#eeeeee",
      300: "#e0e0e0",
      400: "#bdbdbd",
      500: CENTESE_ACCENT_GRAY,
      600: "#757575",
      700: "#616161",
      800: "#424242",
      900: "#212121",
    },
    void: {
      main: "#8c02d1",
      light: "#c24bfd",
      contrastText: "#ffffff",
    },
    holding: {
      main: "#ffa51f",
      light: "#ffb649",
      contrastText: "#000000",
    },
    primaryOrderStatus: {
      draft: "#a19f9f",
      rejected: "#d10217",
      processing: "#fae441",
      complete: "#88fa41",
      void: "#8c02d1",
    },
    approvalStatus: {
      approved: "#4dc900ff",
      pending: "#fae441",
      rejected: "#d10217",
      void: "#8c02d1",
    },
    soFulfillmentStatus: {
      approval: "#a19f9f",
      rejected: "#d10217",
      fulfill: "#4a58d8ff",
      partial: "#fae441",
      full: "#88fa41",
      complete: "#57e200ff",
      void: "#8c02d1",
    },
    poReceiptStatus: {
      approval: "#a19f9f",
      rejected: "#d10217",
      receipt: "#4a58d8ff",
      partial: "#fae441",
      full: "#88fa41",
      complete: "#57e200ff",
      void: "#8c02d1",
    },
    woBuildStatus: {
      rejected: "#d10217",
      complete: "#57e200ff",
      void: "#8c02d1",
      approval: "#a19f9f",
      ready: "#4a58d8ff",
      process: "#fae441",
    },
    woPartStatus: {
      pending: "#4a58d8ff",
      partial: "#fae441",
      complete: "#57e200ff",
      void: "#8c02d1",
    },
    buttonColors: {
      good: "#1ca700ff",
      bad: "#ff0000ff",
    },
  },
  typography: {
    fontFamily: '"Helvetica Neue", "Helvetica", "Arial", sans-serif',
    h1: {
      fontFamily:
        '"Montserrat", "Helvetica Neue", "Helvetica", "Arial", sans-serif',
      fontSize: "2.5rem",
      fontWeight: 700, // Bold
      lineHeight: 1.2,
      color: "#2c2c2c",
    },
    h2: {
      fontFamily:
        '"Montserrat", "Helvetica Neue", "Helvetica", "Arial", sans-serif',
      fontSize: "2rem",
      fontWeight: 700, // Bold
      lineHeight: 1.3,
      color: "#2c2c2c",
    },
    h3: {
      fontFamily:
        '"Montserrat", "Helvetica Neue", "Helvetica", "Arial", sans-serif',
      fontSize: "1.75rem",
      fontWeight: 700, // Bold
      lineHeight: 1.4,
      color: "#2c2c2c",
    },
    h4: {
      fontFamily:
        '"Montserrat", "Helvetica Neue", "Helvetica", "Arial", sans-serif',
      fontSize: "1.5rem",
      fontWeight: 600, // Semi-bold for smaller headers
      lineHeight: 1.4,
      color: "#2c2c2c",
    },
    h5: {
      fontFamily:
        '"Montserrat", "Helvetica Neue", "Helvetica", "Arial", sans-serif',
      fontSize: "1.25rem",
      fontWeight: 600,
      lineHeight: 1.5,
      color: "#2c2c2c",
    },
    h6: {
      fontFamily:
        '"Montserrat", "Helvetica Neue", "Helvetica", "Arial", sans-serif',
      fontSize: "1rem",
      fontWeight: 600,
      lineHeight: 1.6,
      color: "#2c2c2c",
    },
    body1: {
      fontFamily: '"Helvetica Neue", "Helvetica", "Arial", sans-serif',
      fontSize: "1rem",
      lineHeight: 1.5,
      color: "#2c2c2c",
    },
    body2: {
      fontFamily: '"Helvetica Neue", "Helvetica", "Arial", sans-serif',
      fontSize: "0.875rem",
      lineHeight: 1.43,
      color: CENTESE_ACCENT_GRAY,
    },
    subtitle1: {
      fontFamily: '"Helvetica Neue", "Helvetica", "Arial", sans-serif',
      fontSize: "0.75rem",
      lineHeight: 1.25,
      color: "#2c2c2c",
    },
    subtitle2: {
      fontFamily: '"Helvetica Neue", "Helvetica", "Arial", sans-serif',
      fontSize: "0.5rem",
      lineHeight: 1,
      color: "#2c2c2c",
    },
    button: {
      fontFamily: '"Helvetica Neue", "Helvetica", "Arial", sans-serif',
      fontWeight: 500,
      textTransform: "none",
    },
  },
  spacing: 8, // 8px base spacing unit
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1536,
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: ({ theme, ownerState }) => {
          const retVal: { [key: string]: any } = {
            textTransform: "none", // Disable uppercase transformation
            borderRadius: 8,
            fontWeight: 500,
            padding: "8px 16px",
          };
          const btnColors = theme.palette.buttonColors;

          switch (ownerState.color) {
            case "good":
              retVal["backgroundColor"] = btnColors.good;
              retVal["color"] = "#ffffffff";
              break;
            case "bad":
              retVal["backgroundColor"] = btnColors.bad;
              retVal["color"] = "#ffffffff";
              break;
          }

          return retVal;
        },
        contained: {
          boxShadow: "0 2px 4px rgba(0,134,117,0.2)",
          "&:hover": {
            boxShadow: "0 4px 8px rgba(0,134,117,0.3)",
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
          border: "1px solid #e0e0e0",
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: "#f8f9fa",
          "& .MuiTableCell-head": {
            fontWeight: 600,
            color: "#2c2c2c",
            borderBottom: `2px solid ${CENTESE_PRIMARY_GREEN}`,
          },
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          "&:hover": {
            backgroundColor: "rgba(0,134,117,0.04)",
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: CENTESE_PRIMARY_GREEN,
          boxShadow: "0 2px 4px rgba(0,134,117,0.2)",
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: ({ theme, ownerState }) => {
          const retVal: { [key: string]: any } = {
            borderRadius: 16,
          };
          const primStatusColors = theme.palette.primaryOrderStatus;
          const fillStatusColors = theme.palette.soFulfillmentStatus;
          const recStatusColors = theme.palette.poReceiptStatus;
          const appStatusColors = theme.palette.approvalStatus;
          const woBuildColors = theme.palette.woBuildStatus;
          const woPartColors = theme.palette.woPartStatus;

          switch (ownerState.color) {
            case "primdraft":
              retVal["backgroundColor"] = primStatusColors.draft;
              retVal["color"] = "#ffffff";
              break;

            case "primrejected":
              retVal["backgroundColor"] = primStatusColors.rejected;
              retVal["color"] = "#ffffff";
              break;

            case "primprocessing":
              retVal["backgroundColor"] = primStatusColors.processing;
              retVal["color"] = "#2c2c2c";
              break;

            case "primcomplete":
              retVal["backgroundColor"] = primStatusColors.complete;
              retVal["color"] = "#2c2c2c";
              break;

            case "primvoid":
              retVal["backgroundColor"] = primStatusColors.void;
              retVal["color"] = "#ffffff";
              break;

            case "apprejected":
              retVal["backgroundColor"] = appStatusColors.rejected;
              retVal["color"] = "#ffffff";
              break;

            case "apppending":
              retVal["backgroundColor"] = appStatusColors.pending;
              retVal["color"] = "#2c2c2c";
              break;

            case "appapproved":
              retVal["backgroundColor"] = appStatusColors.approved;
              retVal["color"] = "#2c2c2c";
              break;

            case "appvoid":
              retVal["backgroundColor"] = appStatusColors.void;
              retVal["color"] = "#ffffff";
              break;

            case "fillapproval":
              retVal["backgroundColor"] = fillStatusColors.approval;
              retVal["color"] = "#ffffff";
              break;

            case "fillrejected":
              retVal["backgroundColor"] = fillStatusColors.rejected;
              retVal["color"] = "#ffffff";
              break;

            case "fillfulfill":
              retVal["backgroundColor"] = fillStatusColors.fulfill;
              retVal["color"] = "#ffffff";
              break;

            case "fillpartial":
              retVal["backgroundColor"] = fillStatusColors.partial;
              retVal["color"] = "#2c2c2c";
              break;

            case "fillfull":
              retVal["backgroundColor"] = fillStatusColors.full;
              retVal["color"] = "#2c2c2c";
              break;

            case "fillcomplete":
              retVal["backgroundColor"] = fillStatusColors.complete;
              retVal["color"] = "#2c2c2c";
              break;

            case "fillvoid":
              retVal["backgroundColor"] = fillStatusColors.void;
              retVal["color"] = "#ffffff";
              break;

            case "recapproval":
              retVal["backgroundColor"] = recStatusColors.approval;
              retVal["color"] = "#ffffff";
              break;

            case "recrejected":
              retVal["backgroundColor"] = recStatusColors.rejected;
              retVal["color"] = "#ffffff";
              break;

            case "recreceipt":
              retVal["backgroundColor"] = recStatusColors.receipt;
              retVal["color"] = "#ffffff";
              break;

            case "recpartial":
              retVal["backgroundColor"] = recStatusColors.partial;
              retVal["color"] = "#2c2c2c";
              break;

            case "recfull":
              retVal["backgroundColor"] = recStatusColors.full;
              retVal["color"] = "#2c2c2c";
              break;

            case "reccomplete":
              retVal["backgroundColor"] = recStatusColors.complete;
              retVal["color"] = "#2c2c2c";
              break;

            case "recvoid":
              retVal["backgroundColor"] = recStatusColors.void;
              retVal["color"] = "#ffffff";
              break;

            case "buildapproval":
              retVal["backgroundColor"] = woBuildColors.approval;
              retVal["color"] = "#ffffff";
              break;

            case "buildrejected":
              retVal["backgroundColor"] = woBuildColors.rejected;
              retVal["color"] = "#ffffff";
              break;

            case "buildready":
              retVal["backgroundColor"] = woBuildColors.ready;
              retVal["color"] = "#ffffff";
              break;

            case "buildprocess":
              retVal["backgroundColor"] = woBuildColors.process;
              retVal["color"] = "#2c2c2c";
              break;

            case "buildcomplete":
              retVal["backgroundColor"] = woBuildColors.complete;
              retVal["color"] = "#2c2c2c";
              break;

            case "buildvoid":
              retVal["backgroundColor"] = woBuildColors.void;
              retVal["color"] = "#ffffff";
              break;

            case "partpending":
              retVal["backgroundColor"] = woPartColors.pending;
              retVal["color"] = "#ffffff";
              break;

            case "partpartial":
              retVal["backgroundColor"] = woPartColors.partial;
              retVal["color"] = "#2c2c2c";
              break;

            case "partcomplete":
              retVal["backgroundColor"] = woPartColors.complete;
              retVal["color"] = "#2c2c2c";
              break;

            case "partvoid":
              retVal["backgroundColor"] = woPartColors.void;
              retVal["color"] = "#ffffff";
              break;
          }

          return retVal;
        },
      },
    },
  },
});

export type PaletteColorKey = {
  [K in keyof Palette]: Palette[K] extends PaletteColor ? K : never;
}[keyof Palette];

export default theme;
